/**
 * canvasWidgetDefaults — default per-type widget titles + configs (leaf module).
 *
 * Extracted from `CanvasEditor.tsx` by Story 37.3 so the Phase-2 category
 * panels (`ImagesCategoryPanel`, `EmbedsCategoryPanel`) can seed their
 * configure-then-add drafts from the SAME default config the canvas uses on a
 * blank drop — without importing `CanvasEditor` (which would create a runtime
 * import cycle CanvasEditor → categoryPickerRegistry → panel → CanvasEditor,
 * Pre-mortem E). This is a pure relocation: the values are byte-for-byte
 * unchanged, and `CanvasEditor` re-exports them so every existing importer keeps
 * working.
 *
 * LEAF MODULE: depends only on the `WidgetType` type — no component imports, no
 * cycle.
 */

import type { WidgetType } from "./WidgetPalette";

export const DEFAULT_WIDGET_TITLES: Record<WidgetType, string> = {
  kpi: "New KPI",
  "line-chart": "Line Chart",
  "bar-chart": "Bar Chart",
  "pie-chart": "Pie Chart",
  "data-table": "Data Table",
  "text-block": "Text Block",
  "top-posts": "Top Posts",
  "cover-page": "Cover Page",
  funnel: "Funnel",
  demographic: "Demographics",
  heading: "Heading",
  paragraph: "Paragraph",
  "rich-text": "Rich Text",
  "spacer-divider": "Divider",
  image: "Image",
  embed: "Embed",
  "custom-metric": "Custom Metric",
  benchmark: "Benchmark",
  goal: "Goal",
  "rollup-table": "Rollup Table",
};

/**
 * Per-type incrementing title for a NEW widget: "New KPI Card 1", "New KPI Card 2",
 * "New Line Chart 1", … so duplicates of the same type are distinguishable.
 *
 * The number is one past the highest trailing number already used by a same-type
 * widget that still carries the default base name (renamed widgets don't count, so
 * the user's custom titles never bump the sequence). The cover page is a singleton
 * — it keeps its plain "Cover Page" title with no number.
 */
export function nextWidgetTitle(
  type: WidgetType,
  existing: ReadonlyArray<{ type: string; title: string }>,
): string {
  const base = DEFAULT_WIDGET_TITLES[type];
  if (type === "cover-page") return base;
  const escaped = base.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const re = new RegExp(`^${escaped} (\\d+)$`);
  let max = 0;
  for (const w of existing) {
    if (w.type !== type) continue;
    const m = re.exec(w.title);
    if (m) {
      const n = parseInt(m[1]!, 10);
      if (n > max) max = n;
    }
  }
  return `${base} ${max + 1}`;
}

export const DEFAULT_WIDGET_CONFIGS: Record<WidgetType, Record<string, unknown>> = {
  kpi: { metricKey: "", comparisonEnabled: false },
  "line-chart": { metrics: [], xAxis: "date", annotations: [] },
  "bar-chart": { metrics: [], groupBy: "" },
  "pie-chart": { metricKey: "", groupBy: "" },
  "data-table": { columns: [], sortBy: undefined, limit: 10 },
  "text-block": { content: "", variant: "body" },
  "top-posts": { platform: "all", postCount: 5, sortBy: "engagement" },
  "cover-page": { title: "", subtitle: "", preparedBy: "", backgroundStyle: "gradient-blue" },
  funnel: { stages: [], showDropOffRates: true, orientation: "horizontal" },
  demographic: { mode: "device-breakdown", granularity: "country", showTrendIndicator: true },
  heading: { content: "", size: "h2", align: "left" },
  paragraph: { content: "", align: "left" },
  "rich-text": { content: "", variant: "body" },
  "spacer-divider": { style: "line" },
  image: { imageUrl: "", alt: "", fit: "contain", align: "center", caption: "" },
  embed: { url: "", embedUrl: "", provider: "Unknown", aspectRatio: "16:9", showBorder: true },
  "custom-metric": { formulaExpression: "", variables: [], outputFormat: "number", decimalPlaces: 2, comparisonEnabled: false },
  benchmark: { industry: "", metrics: [], showPercentileRange: true, showBadge: true, metricVisibility: {} },
  goal: { goalType: "", targetValue: 0, unit: "number", currencyCode: "USD", periodType: "monthly", showPacing: true, showPercentage: true, showDaysRemaining: true, thresholds: { warning: 80, danger: 100 } },
  // Story 40-5 — per-client rows + totals/averages footer; `metrics` are the columns.
  "rollup-table": { metrics: [], showAverages: false },
};
